/**
 * Maps Attendance / Payroll wizard step keys → RBAC screen keys.
 * Hub screens (process_attendance / run_payroll) open the wizard;
 * these screens gate individual steps.
 */

export const ATTENDANCE_STEP_RBAC = {
  change_status: "att_act_change_status",
  apply_leave: "att_act_apply_leave",
  lop_reversal: "att_act_lop_reversal",
  reconciliation: "att_proc_reconciliation",
  process: "att_proc_process",
  checklist: "att_proc_checklist",
  preview_validate: "att_proc_preview_validate",
  finalise: "att_proc_finalise",
};

export const PAYROLL_STEP_RBAC = {
  new_joinees: "pay_proc_new_joinees",
  left_employees: "pay_proc_left_employees",
  salary_revisions: "pay_proc_salary_revisions",
  attendance_validation: "pay_proc_attendance_validation",
  lop_reversal_arrears: "pay_proc_lop_reversal_arrears",
  one_time_inputs: "pay_proc_one_time_inputs",
  bank_details: "pay_proc_bank_details",
  headcount_reconcile: "pay_proc_headcount_reconcile",
  process: "pay_proc_process",
  checklist: "pay_proc_checklist",
  company_checklist: "pay_proc_checklist",
  disbursement: "pay_proc_disbursement",
  preview_validate: "pay_proc_preview_validate",
  finalise: "pay_proc_finalise",
  payslips: "pay_proc_payslips",
};

function hubAllowsView(hasScreenAccess, moduleKey) {
  if (moduleKey === "attendance") {
    return (
      hasScreenAccess("attendance", "process_attendance", "view") ||
      hasScreenAccess("attendance", "process_attendance", "edit") ||
      hasScreenAccess("attendance", "process_attendance", "approve")
    );
  }
  if (moduleKey === "payroll") {
    return (
      hasScreenAccess("payroll", "run_payroll", "view") ||
      hasScreenAccess("payroll", "run_payroll", "create") ||
      hasScreenAccess("payroll", "run_payroll", "edit") ||
      hasScreenAccess("payroll", "process_payroll", "view") ||
      hasScreenAccess("payroll", "process_payroll", "create")
    );
  }
  return false;
}

function hubAllowsEdit(hasScreenAccess, moduleKey) {
  if (moduleKey === "attendance") {
    return (
      hasScreenAccess("attendance", "process_attendance", "edit") ||
      hasScreenAccess("attendance", "process_attendance", "approve")
    );
  }
  if (moduleKey === "payroll") {
    return (
      hasScreenAccess("payroll", "run_payroll", "create") ||
      hasScreenAccess("payroll", "run_payroll", "edit") ||
      hasScreenAccess("payroll", "run_payroll", "approve") ||
      hasScreenAccess("payroll", "process_payroll", "create") ||
      hasScreenAccess("payroll", "process_payroll", "edit")
    );
  }
  return false;
}

/**
 * True when the user may see the step.
 * Hub-only roles are expanded to att_proc_* / pay_proc_* on login — steps are explicit.
 * Do not inherit every step from Open wizard View (that blocked payslip-only grants).
 */
export function canViewProcessStep(hasScreenAccess, moduleKey, stepKey, map) {
  const screen = map?.[stepKey];
  if (!screen) return true;
  if (typeof hasScreenAccess !== "function") return true;
  if (hasScreenAccess(moduleKey, screen, "view")) return true;
  if (String(screen).startsWith("att_act_") || String(screen).startsWith("pay_act_")) return false;
  // Legacy: no process-step rows yet — hub View still opens the full pipeline.
  const stepPrefix = moduleKey === "attendance" ? "att_proc_" : "pay_proc_";
  if (String(screen).startsWith(stepPrefix)) {
    // If this role already uses process-step keys, missing view means hidden.
    // Hub fallback only when the step key itself was never granted (expand handles hub-only).
    return false;
  }
  return hubAllowsView(hasScreenAccess, moduleKey);
}

export function canEditProcessStep(hasScreenAccess, moduleKey, stepKey, map) {
  const screen = map?.[stepKey];
  if (!screen) return true;
  if (typeof hasScreenAccess !== "function") return true;
  if (
    hasScreenAccess(moduleKey, screen, "edit") ||
    hasScreenAccess(moduleKey, screen, "create") ||
    hasScreenAccess(moduleKey, screen, "approve")
  ) {
    return true;
  }
  if (String(screen).startsWith("att_act_") || String(screen).startsWith("pay_act_")) return false;
  // View-only on the step means browse the step — do not inherit Process/Reprocess from hub.
  if (hasScreenAccess(moduleKey, screen, "view")) return false;
  return hubAllowsEdit(hasScreenAccess, moduleKey);
}

export function canApproveProcessStep(hasScreenAccess, moduleKey, stepKey, map) {
  const screen = map?.[stepKey];
  if (!screen) return true;
  if (typeof hasScreenAccess !== "function") return true;
  if (hasScreenAccess(moduleKey, screen, "approve") || hasScreenAccess(moduleKey, screen, "edit")) {
    return true;
  }
  if (String(screen).startsWith("att_act_") || String(screen).startsWith("pay_act_")) return false;
  // View-only Finalise step: no lock/unlock from hub mutate.
  if (hasScreenAccess(moduleKey, screen, "view")) return false;
  return hubAllowsEdit(hasScreenAccess, moduleKey);
}
